import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/contexts/AppContext";

import Landing from "@/pages/Landing";
import OwnerLogin from "@/pages/OwnerLogin";
import StaffLogin from "@/pages/StaffLogin";
import PublicMenu from "@/pages/PublicMenu";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";

import Dashboard from "@/pages/Dashboard";
import MenuManagement from "@/pages/MenuManagement";
import StaffManagement from "@/pages/StaffManagement";
import TableManagement from "@/pages/TableManagement";
import Settings from "@/pages/Settings";
import OrdersList from "@/pages/OrdersList";
import WaiterHome from "@/pages/WaiterHome";
import WaiterNewOrder from "@/pages/WaiterNewOrder";
import CashierHome from "@/pages/CashierHome";
import DriverHome from "@/pages/DriverHome";
import QrPrintSheet from "@/pages/QrPrintSheet";
import Reports from "@/pages/Reports";

function RoleHome() {
    const { user } = useApp();
    if (user === null) return null;
    if (!user) return <Navigate to="/" replace />;
    const dest = {
        owner: "/dashboard",
        manager: "/dashboard",
        cashier: "/cashier",
        waiter: "/waiter",
        kitchen: "/kitchen",
        driver: "/driver",
    }[user.role] || "/";
    return <Navigate to={dest} replace />;
}

function RootRedirect() {
    const { user } = useApp();
    // Still loading auth state — show nothing
    if (user === null) return null;
    // Logged-in staff/owner → their dashboard
    if (user) {
        const dest = {
            owner: "/dashboard",
            manager: "/dashboard",
            cashier: "/cashier",
            waiter: "/waiter",
            kitchen: "/kitchen",
            driver: "/driver",
        }[user.role] || "/menu";
        return <Navigate to={dest} replace />;
    }
    // Unauthenticated visitor (customer) → public menu directly
    return <Navigate to="/menu" replace />;
}

function App() {
    return (
        <AppProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/login" element={<OwnerLogin />} />
                    <Route path="/staff-login" element={<StaffLogin />} />
                    <Route path="/menu" element={<PublicMenu />} />

                    <Route path="/app" element={<RoleHome />} />

                    {/* Owner + Manager dashboard */}
                    <Route element={<ProtectedRoute roles={["owner", "manager"]} />}>
                        <Route element={<Layout />}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/dashboard/menu" element={<MenuManagement />} />
                            <Route path="/dashboard/tables" element={<TableManagement />} />
                            <Route path="/dashboard/orders" element={<OrdersList />} />
                            <Route path="/dashboard/staff" element={<StaffManagement />} />
                            <Route path="/dashboard/settings" element={<Settings />} />
                            <Route path="/dashboard/qr-print" element={<QrPrintSheet />} />
                            <Route path="/dashboard/reports" element={<Reports />} />
                        </Route>
                    </Route>

                    {/* Cashier */}
                    <Route element={<ProtectedRoute roles={["cashier", "manager", "owner"]} />}>
                        <Route element={<Layout />}>
                            <Route path="/cashier" element={<CashierHome />} />
                            <Route path="/cashier/tables" element={<TableManagement />} />
                        </Route>
                    </Route>

                    {/* Waiter */}
                    <Route element={<ProtectedRoute roles={["waiter", "manager", "owner"]} />}>
                        <Route element={<Layout />}>
                            <Route path="/waiter" element={<WaiterHome />} />
                            <Route path="/waiter/orders" element={<OrdersList />} />
                            <Route path="/waiter/new" element={<WaiterNewOrder />} />
                        </Route>
                    </Route>

                    {/* Driver — uses its own minimal layout */}
                    <Route element={<ProtectedRoute roles={["driver", "manager", "owner"]} />}>
                        <Route path="/driver" element={<DriverHome />} />
                    </Route>
                </Routes>
                <InstallPrompt />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
        </AppProvider>
    );
}

export default App;
